import { Button } from '@mdvip/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@mdvip/shared/components/ui/dialog';
import { useTranslation } from 'react-i18next';

interface IdleTimeoutWarningDialogProps {
  open: boolean;
  remainingSeconds: number;
  onContinue: () => void;
  onLogout: () => void;
}

export function IdleTimeoutWarningDialog({
  open,
  remainingSeconds,
  onContinue,
  onLogout,
}: IdleTimeoutWarningDialogProps) {
  const { t } = useTranslation('shared');

  return (
    // Controlled `open` with no onOpenChange: backdrop/escape dismissal requests are dropped, so an
    // idle warning forces an explicit choice. Open state is driven entirely by the parent.
    <Dialog open={open}>
      <DialogContent showCloseButton={false} className="max-w-md">
        <div className="flex flex-col gap-4">
          <div className="space-y-1">
            <DialogTitle className="text-lg font-semibold leading-none tracking-tight">{t('idle.title')}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">{t('idle.description')}</DialogDescription>
          </div>

          <div className="flex flex-col items-center gap-1 py-2">
            {/* aria-live="assertive" announces the countdown to screen readers each second */}
            <p className="text-5xl font-bold tabular-nums" aria-live="assertive" aria-atomic="true">
              {remainingSeconds}
            </p>
            <p className="text-xs text-muted-foreground">{t('idle.countdown', { count: remainingSeconds })}</p>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={onLogout}>
              {t('idle.logOut')}
            </Button>
            <Button onClick={onContinue}>{t('idle.stayLoggedIn')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
